import type { ComponentPropsWithoutRef, ElementType, ReactNode } from "react";
import { cx } from "../../cx";
import { buttonVariantClasses, type ButtonSize, type ButtonVariant } from "./Button";

const SIZE: Readonly<Record<ButtonSize, string>> = {
  sm: "h-9 gap-1.5 px-3 text-sm",
  md: "h-11 gap-2 px-5 text-sm",
  lg: "h-12 gap-2 px-6 text-base",
};

/** Same focus ring as `Button`, shared so links, tabs and chips get the identical outline (CL-2). */
export const focusRingClasses =
  "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus";

export type ButtonLinkProps<C extends ElementType = "a"> = {
  /** Router link component (e.g. react-router `Link`); defaults to a plain `<a>`. */
  readonly as?: C;
  readonly variant?: ButtonVariant;
  readonly size?: ButtonSize;
  readonly startIcon?: ReactNode;
  readonly endIcon?: ReactNode;
} & Omit<ComponentPropsWithoutRef<C>, "as" | "className"> & { readonly className?: string };

/** A link that looks like `Button` (same variants, sizes, focus ring), for navigation actions. */
export function ButtonLink<C extends ElementType = "a">({
  as,
  variant = "primary",
  size = "md",
  startIcon,
  endIcon,
  className,
  children,
  ...rest
}: ButtonLinkProps<C>) {
  const Tag: ElementType = as ?? "a";
  return (
    <Tag
      className={cx(
        "relative inline-flex items-center justify-center rounded-xl font-medium whitespace-nowrap no-underline transition-colors select-none",
        focusRingClasses,
        buttonVariantClasses[variant],
        SIZE[size],
        className,
      )}
      {...rest}
    >
      {startIcon}
      <span>{children as ReactNode}</span>
      {endIcon}
    </Tag>
  );
}
